import { useEffect, useMemo, useRef, useState } from 'react';

import { useDragAndDrop } from '@formkit/drag-and-drop/react';
import { FORM_FIELD_PREFIX } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { ColumnDef } from '@tanstack/react-table';

import ColumnsIcon from '@/components/icons/columns';
import GripDotVerticalIcon from '@/components/icons/grip-dot-vertical';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { ModuleTableColumn } from '@/stores/modules.store';

import { TablePageHeaderActionButton } from './table-page-header';

function ColumnsDialog<T>({
  open,
  onOpenChange,
  columns,
  columnOrder,
  onColumnOrderChange,
  noTrigger,
  disabled,
  defaultSectionLabel,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  columns: ColumnDef<T>[];
  columnOrder: { id: string; active: boolean }[];
  onColumnOrderChange: (columnOrder: { id: string; active: boolean }[]) => void;
  noTrigger?: boolean;
  disabled?: boolean;
  /** Section label for columns that carry no `meta.columnGroup` of their own. */
  defaultSectionLabel?: string;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {!noTrigger && (
        <DialogTrigger asChild>
          <TablePageHeaderActionButton icon={<ColumnsIcon />} label="Edit Columns" disabled={disabled} />
        </DialogTrigger>
      )}

      <DialogContent className="!top-1/2 flex max-h-[calc(100vh-200px)] !-translate-y-1/2 flex-col gap-[10px] overflow-hidden">
        <DialogTitle>Edit Columns</DialogTitle>

        <DialogDescription>
          You can control which fields are visible and what order they appear by toggling the visibility on/off and dragging to rearrange.
        </DialogDescription>

        <ColumnsDialogControls
          onOpenChange={onOpenChange}
          columns={columns}
          columnOrder={columnOrder}
          onColumnOrderChange={onColumnOrderChange}
          defaultSectionLabel={defaultSectionLabel}
        />
      </DialogContent>
    </Dialog>
  );
}

const SCROLL_SHADOW_GRADIENT = 'radial-gradient(ellipse 60% 80% at 50% var(--pos), rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.03) 50%, transparent 100%)';

function ScrollShadow({ position, visible }: { position: 'top' | 'bottom'; visible: boolean }) {
  return (
    <div
      className={cn(
        'pointer-events-none absolute right-0 left-0 z-10 h-6 transition-opacity duration-300',
        position === 'top' ? 'top-0' : 'bottom-0',
        visible ? 'opacity-100' : 'opacity-0',
      )}
      style={{ '--pos': position === 'top' ? '0%' : '100%', background: SCROLL_SHADOW_GRADIENT } as React.CSSProperties}
    />
  );
}

function isFormFieldColumn(columnId: string): boolean {
  return columnId.startsWith(FORM_FIELD_PREFIX);
}

function ColumnsDialogControls<T>({
  onOpenChange,
  columns,
  columnOrder,
  onColumnOrderChange,
  defaultSectionLabel,
}: {
  onOpenChange: (open: boolean) => void;
  columns: ColumnDef<T>[];
  columnOrder: { id: string; active: boolean }[];
  onColumnOrderChange: (columnOrder: { id: string; active: boolean }[]) => void;
  defaultSectionLabel?: string;
}) {
  // `columns` is the authoritative set (it always carries the form-field columns
  // once the form template loads); `columnOrder` supplies order + visibility but can
  // lag behind it. Reconcile against `columns` so a column missing from a stale
  // `columnOrder` is still shown (default hidden) instead of dropping out of the panel.
  const allColumnIds = useMemo(
    () => columns.map((col) => col.id ?? (col as { accessorKey?: string }).accessorKey).filter((id): id is string => !!id),
    [columns],
  );

  const reconciledColumns = useMemo<ModuleTableColumn[]>(() => {
    const validIds = new Set(allColumnIds);
    const orderedIds = new Set(columnOrder.map((col) => col.id));
    const ordered = columnOrder.filter((col) => validIds.has(col.id));
    const missing = allColumnIds.filter((id) => !orderedIds.has(id)).map((id) => ({ id, active: false }));
    return [...ordered, ...missing];
  }, [columnOrder, allColumnIds]);

  const selectColumn = useMemo(() => reconciledColumns.find((col) => col.id === 'select'), [reconciledColumns]);
  const columnsWithoutSelect = useMemo(() => reconciledColumns.filter((col) => col.id !== 'select'), [reconciledColumns]);

  const metadataColumns = useMemo(() => columnsWithoutSelect.filter((col) => !isFormFieldColumn(col.id)), [columnsWithoutSelect]);
  const formFieldColumns = useMemo(() => columnsWithoutSelect.filter((col) => isFormFieldColumn(col.id)), [columnsWithoutSelect]);

  const [tempMetadataColumns, setTempMetadataColumns] = useState<ModuleTableColumn[]>(metadataColumns);
  const [tempFormFieldColumns, setTempFormFieldColumns] = useState<ModuleTableColumn[]>(formFieldColumns);

  const tempMetadataColumnsRef = useRef(tempMetadataColumns);
  const tempFormFieldColumnsRef = useRef(tempFormFieldColumns);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showTopShadow, setShowTopShadow] = useState(false);
  const [showBottomShadow, setShowBottomShadow] = useState(false);

  useEffect(() => {
    tempMetadataColumnsRef.current = tempMetadataColumns;
  }, [tempMetadataColumns]);

  useEffect(() => {
    tempFormFieldColumnsRef.current = tempFormFieldColumns;
  }, [tempFormFieldColumns]);

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;

    const handleScroll = () => {
      setShowTopShadow(el.scrollTop > 0);
      setShowBottomShadow(el.scrollTop + el.clientHeight < el.scrollHeight);
    };

    handleScroll();
    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, []);

  const [metadataListRef, metadataList] = useDragAndDrop<HTMLUListElement, ModuleTableColumn>(tempMetadataColumns, {
    dragHandle: '.metadata-handle',
    onSort: ({ values }) => {
      const mergedColumns = values.map((col) => {
        const existingColumn = tempMetadataColumnsRef.current.find((c) => c.id === (col as ModuleTableColumn).id);
        return existingColumn ? { ...existingColumn } : (col as ModuleTableColumn);
      });
      setTempMetadataColumns(mergedColumns as ModuleTableColumn[]);
    },
  });

  const [formFieldListRef, formFieldList] = useDragAndDrop<HTMLUListElement, ModuleTableColumn>(tempFormFieldColumns, {
    dragHandle: '.form-field-handle',
    onSort: ({ values }) => {
      const mergedColumns = values.map((col) => {
        const existingColumn = tempFormFieldColumnsRef.current.find((c) => c.id === (col as ModuleTableColumn).id);
        return existingColumn ? { ...existingColumn } : (col as ModuleTableColumn);
      });
      setTempFormFieldColumns(mergedColumns as ModuleTableColumn[]);
    },
  });

  const handleSave = () => {
    const finalColumns = selectColumn ? [selectColumn, ...tempMetadataColumns, ...tempFormFieldColumns] : [...tempMetadataColumns, ...tempFormFieldColumns];

    onColumnOrderChange(finalColumns);
    onOpenChange(false);
  };

  const findColumn = (columnId: string) => columns.find((c) => (c.id ? c.id === columnId : (c as { accessorKey: string }).accessorKey === columnId));

  const getColumnHeader = (columnId: string) => {
    const header = findColumn(columnId)?.header;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return typeof header === 'function' ? (header as (ctx: any) => string)({}) : header?.toString() || '';
  };

  const getSectionLabel = (columnId: string) => (isFormFieldColumn(columnId) ? undefined : (findColumn(columnId)?.meta?.columnGroup ?? defaultSectionLabel));

  const renderSectionLabel = (columnId: string) => {
    const label = getSectionLabel(columnId);
    return label ? (
      <Badge variant="grey" size="sm" className="font-medium">
        {label}
      </Badge>
    ) : null;
  };

  return (
    <div>
      <div className="relative">
        <ScrollShadow position="top" visible={showTopShadow} />
        <ScrollShadow position="bottom" visible={showBottomShadow} />
        {/* The `flex min-h-0 flex-1` scroll pattern in update-workspace-entity-dialog.tsx is more robust than this fixed viewport-offset cap and is preferred for new dialogs of this shape. */}
        <div ref={scrollContainerRef} className="max-h-[calc(100vh-400px)] overflow-y-auto pr-2">
          <ul ref={metadataListRef} className="columns">
            {metadataList.map((column) => (
              <li key={column.id}>
                <label className="metadata-handle hover:bg-grey-1 flex cursor-pointer items-center justify-between gap-2 pr-2">
                  <GripDotVerticalIcon className="text-secondary-text" />
                  <div className="flex w-full items-center justify-between gap-2">
                    <div className="flex items-center gap-1 p-[5px] text-sm">
                      {getColumnHeader(column.id)}
                      {renderSectionLabel(column.id)}
                    </div>
                    <ColumnsDialogControlsCheckbox id={column.id} tempColumns={tempMetadataColumns} setTempColumns={setTempMetadataColumns} />
                  </div>
                </label>
              </li>
            ))}
          </ul>

          {formFieldList.length > 0 && (
            <>
              <div className="text-secondary-text mt-2 text-xs">Form Fields</div>
              <ul ref={formFieldListRef} className="columns">
                {formFieldList.map((column) => (
                  <li key={column.id}>
                    <label className="form-field-handle hover:bg-grey-1 flex cursor-pointer items-center justify-between gap-2 pr-2">
                      <GripDotVerticalIcon className="text-secondary-text" />
                      <div className="flex w-full items-center justify-between gap-2">
                        <div className="flex items-center gap-1 p-[5px] text-sm">
                          {getColumnHeader(column.id)}
                          {renderSectionLabel(column.id)}
                        </div>
                        <ColumnsDialogControlsCheckbox id={column.id} tempColumns={tempFormFieldColumns} setTempColumns={setTempFormFieldColumns} />
                      </div>
                    </label>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </div>

      <div className="pt-3">
        <Button variant="success" className="w-fit" onClick={handleSave}>
          Save changes
        </Button>
      </div>
    </div>
  );
}

function ColumnsDialogControlsCheckbox({
  id,
  tempColumns,
  setTempColumns,
}: {
  id: string;
  tempColumns: ModuleTableColumn[];
  setTempColumns: (columns: ModuleTableColumn[]) => void;
}) {
  const column = tempColumns.find((c) => c.id === id);
  if (!column) return null;

  return (
    <Checkbox
      className="size-3"
      checked={column.active}
      onCheckedChange={(checked) => setTempColumns(tempColumns.map((c) => (c.id === column.id ? { ...c, active: checked as boolean } : c)))}
    />
  );
}

export default ColumnsDialog;
